import React, { useState, useEffect } from 'react';
import {
  Modal,
  Offcanvas,
  Button,
  Form,
  Row,
  Col
} from 'react-bootstrap';
import { DayPicker } from 'react-day-picker';
import { Search as SearchIcon, CalendarCheck, Users } from 'lucide-react';
import 'react-day-picker/dist/style.css';

const MOBILE_BREAKPOINT = 768;

export default function TourSearchModal({
  show,
  onHide,
  durationDays,
  members ,
  minDays ,
  maxDays ,
  minMembers ,
  maxMembers,
  onApply
}) {

  const [startDate, setStartDate] = useState();
  const [selectedDays, setSelectedDays] = useState(durationDays || 0);
  const [selectedMembers, setSelectedMembers] = useState(members || 0);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  const STORAGE_KEY = 'tourBookingSearch';
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  useEffect(() => {
    if (!show) return;

    const saved = localStorage.getItem(STORAGE_KEY);

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const savedStart = parsed?.from ? new Date(parsed.from) : undefined;
        const savedDays = parsed?.days ? Number(parsed.days) : Number(durationDays || 1);
        const savedMembers = parsed?.members ? Number(parsed.members) : Number(members || 1);

        setSelectedDays(savedDays);
        setSelectedMembers(savedMembers);

        if (savedStart) {
          const end = new Date(savedStart);
          end.setDate(end.getDate() + savedDays - 1);

          setStartDate(savedStart);
          setRange({
            from: savedStart,
            to: end,
          });
        } else {
          setStartDate(undefined);
          setRange({ from: undefined, to: undefined });
        }
      } catch {
        setStartDate(undefined);
        setSelectedDays(Number(durationDays || 1));
        setSelectedMembers(Number(members || 1));
        setRange({ from: undefined, to: undefined });
      }
    } else {
      setStartDate(undefined);
      setSelectedDays(Number(durationDays || 1));
      setSelectedMembers(Number(members || 1));
      setRange({ from: undefined, to: undefined });
    }
  }, [show, durationDays, members]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!startDate || !selectedDays) return;

    const end = new Date(startDate);
    end.setDate(end.getDate() + Number(selectedDays) - 1);

    setRange({
      from: startDate,
      to: end,
    });
  }, [startDate, selectedDays]);

  const handleDateSelect = (selected) => {
    if (!selected) return;

    const end = new Date(selected);
    end.setDate(end.getDate() + Number(selectedDays) - 1);

    setStartDate(selected);
    setRange({
      from: selected,
      to: end,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!startDate || !range.to) {
      alert('Please select a start date.');
      return;
    }

    const payload = {
      from: startDate.toISOString(),
      to: range.to.toISOString(),
      days: selectedDays,
      members: selectedMembers
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));

    if (onApply) {
      onApply({
        from: startDate,
        to: range.to,
        days: selectedDays,
        members: selectedMembers
      });
    }

    onHide();
  };

  const dayOptions = [];
  for (let i = Number(minDays); i <= Number(maxDays); i++) {
    dayOptions.push(i);
  }

  const memberOptions = [];
  for (let i = Number(minMembers); i <= Number(maxMembers); i++) {
    memberOptions.push(i);
  }

  const content = (
    <Form onSubmit={handleSubmit}>
      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-semibold d-flex align-items-center gap-2">
              <Users size={16} />
              Guests
            </Form.Label>
            <Form.Select
              value={selectedMembers}
              onChange={(e) => setSelectedMembers(Number(e.target.value))}
            >
              {memberOptions.map((item) => (
                <option key={item} value={item}>
                  {item} {item === 1 ? 'Guest' : 'Guests'}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-semibold d-flex align-items-center gap-2">
              <CalendarCheck size={16} />
              Days
            </Form.Label>
            <Form.Select
              value={selectedDays}
              onChange={(e) => setSelectedDays(Number(e.target.value))}
            >
              {dayOptions.map((item) => (
                <option key={item} value={item}>
                  {item} {item === 1 ? 'Day' : 'Days'}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col xs={12}>
          <Form.Group className="mb-0">
            <Form.Label className="fw-semibold">Select Start Date</Form.Label>
            <div className="border rounded-4 p-3 bg-white">
              <center>
              <DayPicker
                mode="single"
                selected={startDate}
                onSelect={handleDateSelect}
                disabled={{ before: tomorrow }}
                
              />
              </center>
            </div>

            {range.from && range.to && (
              <div className="mt-3 p-3 rounded-4" style={{ background: '#f8fff4' }}>
                <p className="mb-1 text-success fw-semibold">
                  Your trip plan
                </p>
                <p className="mb-1 small">
                  <strong>Dates:</strong> {range.from.toDateString()} to {range.to.toDateString()}
                </p>
                <p className="mb-1 small">
                  <strong>Duration:</strong> {selectedDays} {selectedDays === 1 ? 'Day' : 'Days'}
                </p>
                <p className="mb-0 small">
                  <strong>Guests:</strong> {selectedMembers} {selectedMembers === 1 ? 'Guest' : 'Guests'}
                </p>
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Button type="submit" className="w-100 btn-own mt-3">
        <SearchIcon className="me-2" size={18} />
        Apply
      </Button>
    </Form>
  );

  return isMobile ? (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="bottom"
      style={{ borderTopLeftRadius: 20, borderTopRightRadius: 20, height: 'auto' }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <SearchIcon className="me-2" /> Tour Search
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>{content}</Offcanvas.Body>
    </Offcanvas>
  ) : (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <SearchIcon className="me-2" /> Tour Search
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{content}</Modal.Body>
    </Modal>
  );
}